/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useCallback } from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { formatPrice } from '../../../state/offerings';
import { useBrandTheme, getCtaClasses } from '../brand-theme';
import type { BookingSummaryMobileProps, BookingStepV4 } from '../types';

/**
 * Mobile booking summary sticky bottom bar.
 * Compact view with expandable details drawer.
 */
export const BookingSummaryMobile: React.FC<BookingSummaryMobileProps> = ({
  selectedPackage,
  selectedRooms,
  dateRange,
  guestCounts,
  pricing,
  currentStep,
  onProceed,
  canProceed,
  messages,
  getTentConfig,
}) => {
  const { theme } = useBrandTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const hasRooms = selectedRooms.length > 0;
  const hasDates = dateRange.start && dateRange.end;
  const totalRooms = selectedRooms.reduce((sum, [, count]) => sum + count, 0);
  const totalGuests = guestCounts.adults + guestCounts.children;

  // Get contextual CTA text based on current step
  const ctaText = getCtaText(currentStep, canProceed, messages);

  return (
    <>
      {/* Expandable details drawer */}
      {isExpanded && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={toggleExpanded}
        >
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[70vh] overflow-y-auto animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer handle */}
            <div className="sticky top-0 bg-white pt-3 pb-2 flex justify-center">
              <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
            </div>

            {/* Drawer content */}
            <div className="px-5 pb-5 space-y-4">
              <h3 className="font-semibold text-lg text-gray-900">Booking summary</h3>

              {/* Package */}
              {selectedPackage && (
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">{messages.packageLabel}</span>
                  <span className="font-medium text-gray-900">{selectedPackage.name}</span>
                </div>
              )}

              {/* Rooms */}
              <div className="py-2 border-b border-gray-100">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">{messages.roomsLabel}</span>
                </div>
                {hasRooms ? (
                  <div className="space-y-1">
                    {selectedRooms.map(([type, count]) => {
                      const config = getTentConfig?.(type);
                      return (
                        <div key={type} className="flex justify-between text-sm">
                          <span className="text-gray-900">{config?.title || type}</span>
                          <span className={clsx('font-medium', theme.accentText)}>×{count}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <span className="text-gray-400 text-sm">No rooms selected</span>
                )}
              </div>

              {/* Dates */}
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">{messages.datesLabel}</span>
                <span className="font-medium text-gray-900">
                  {hasDates
                    ? `${format(new Date(dateRange.start), 'MMM d')} - ${format(new Date(dateRange.end), 'MMM d')}`
                    : '--'}
                </span>
              </div>

              {/* Guests */}
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-600">{messages.guestsLabel}</span>
                <span className="font-medium text-gray-900">
                  {totalGuests > 0
                    ? `${guestCounts.adults} ${guestCounts.adults === 1 ? messages.adult : messages.adults}${guestCounts.children > 0 ? `, ${guestCounts.children} ${guestCounts.children === 1 ? messages.child : messages.children}` : ''}`
                    : '--'}
                </span>
              </div>

              {/* Price breakdown */}
              {pricing.total > 0 && (
                <>
                  {/* Accommodation */}
                  {pricing.accommodationTotal > 0 && (
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">
                        {totalRooms} {totalRooms === 1 ? 'room' : 'rooms'} × {pricing.nights}{' '}
                        {pricing.nights === 1 ? messages.night : messages.nights}
                      </span>
                      <span className="text-gray-900">
                        {formatPrice({ amount: pricing.accommodationTotal, currency: 'INR' })}
                      </span>
                    </div>
                  )}
                  {pricing.accommodationGst > 0 && (
                    <div className="flex justify-between py-1">
                      <span className="text-gray-500 text-sm pl-2">
                        GST ({pricing.gstPercentage}%)
                      </span>
                      <span className="text-gray-600 text-sm">
                        {formatPrice({ amount: pricing.accommodationGst, currency: 'INR' })}
                      </span>
                    </div>
                  )}

                  {/* Package total (aggregated - no per-item breakdown) */}
                  {selectedPackage && pricing.packageTotal > 0 && (
                    <>
                      <div className="flex justify-between py-2 pt-4 mt-2 border-t border-gray-100">
                        <span className={clsx('text-gray-700 font-medium', theme.accentText)}>
                          {selectedPackage.name}
                        </span>
                        <span className="text-gray-900">
                          {formatPrice({ amount: pricing.packageTotal, currency: 'INR' })}
                        </span>
                      </div>
                      {pricing.packageGst > 0 && (
                        <div className="flex justify-between py-1">
                          <span className="text-gray-500 text-sm pl-2">
                            GST ({pricing.packageGstPercentage}%)
                          </span>
                          <span className="text-gray-600 text-sm">
                            {formatPrice({ amount: pricing.packageGst, currency: 'INR' })}
                          </span>
                        </div>
                      )}
                    </>
                  )}

                  {/* Grand total */}
                  <div className="flex justify-between pt-3 border-t border-gray-200">
                    <span className="font-semibold text-gray-900">{messages.grandTotalLabel}</span>
                    <span className="font-bold text-lg text-gray-900">
                      {formatPrice({ amount: pricing.totalWithGst, currency: 'INR' })}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bottom bar */}
      <div className="flex items-center justify-between px-4 py-3 h-16">
        {/* Price display */}
        <button
          type="button"
          onClick={toggleExpanded}
          className="flex items-center gap-2 text-left"
        >
          <div>
            {pricing.totalWithGst > 0 ? (
              <>
                <p className="text-lg font-bold text-gray-900">
                  {formatPrice({ amount: pricing.totalWithGst, currency: 'INR' })}
                </p>
                <p className="text-xs text-gray-500">
                  {pricing.nights > 0
                    ? `${pricing.nights} ${pricing.nights === 1 ? messages.night : messages.nights}`
                    : messages.totalLabel}
                </p>
              </>
            ) : (
              <>
                <p className="text-base font-semibold text-gray-900">View details</p>
                <p className="text-xs text-gray-500">Select options to see price</p>
              </>
            )}
          </div>
          <ChevronUpIcon
            className={clsx(
              'w-5 h-5 text-gray-400 transition-transform duration-200',
              isExpanded && 'rotate-180',
            )}
          />
        </button>

        {/* CTA Button */}
        <button
          type="button"
          onClick={onProceed}
          disabled={!canProceed}
          className={clsx(
            'py-3 px-6 rounded-xl font-semibold text-sm',
            'transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-offset-2',
            canProceed
              ? `${theme.ctaBg} text-white ${theme.ctaHover} shadow-lg ${theme.ctaRing}`
              : 'bg-gray-100 text-gray-400 cursor-not-allowed',
          )}
        >
          {ctaText}
        </button>
      </div>
    </>
  );
};

function getCtaText(
  step: BookingStepV4,
  canProceed: boolean,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  messages: BookingSummaryMobileProps['messages'],
): string {
  if (!canProceed) {
    switch (step) {
      case 'room-selection':
        return 'Select rooms';
      case 'date-guest':
        return 'Select dates';
      case 'guest-details':
        return 'Enter details';
      default:
        return 'Continue';
    }
  }

  if (step === 'guest-details') {
    return 'To payment';
  }

  return 'Continue';
}

// Icon component
const ChevronUpIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
  </svg>
);

export default BookingSummaryMobile;
